/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import {
  ClipboardButton,
  type CodeBlock,
  LoadingSpinner,
  StatusLabel,
  Typography,
  useNeedleTheme,
} from '@neo4j-ndl/react';
import { CheckIconOutline, XMarkIconOutline } from '@neo4j-ndl/react/icons';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';

import { type CommonProps } from '../../_common/types';
import {
  type LanguageProp,
  Languages,
  loadLanguage,
} from '../../code-block/code-languages';
import ndlCodeDark from '../../code-block/themes/ndl-code-dark';
import ndlCodeLight from '../../code-block/themes/ndl-code-light';

type PreviewProps = {
  children: React.ReactNode;
};

/**
 * Component to use for previewing certain content in AI chat applications.
 *
 * @alpha - Changes to this component may be breaking.
 */
const PreviewComponent = ({
  children,
  ref,
  style,
  className,
  htmlAttributes,
  ...restProps
}: CommonProps<'div', PreviewProps>) => {
  return (
    <div
      ref={ref}
      style={style}
      className={classNames('ndl-ai-preview', className)}
      {...restProps}
      {...htmlAttributes}
    >
      {children}
    </div>
  );
};

type PreviewHeaderProps = {
  children?: React.ReactNode;
  actions?: React.ReactNode;
};

const PreviewHeader = ({
  children,
  actions,
  ref,
  style,
  className,
  htmlAttributes,
  ...restProps
}: CommonProps<'div', PreviewHeaderProps>) => {
  return (
    <div
      ref={ref}
      style={style}
      className={classNames('ndl-ai-preview-header', className)}
      {...restProps}
      {...htmlAttributes}
    >
      <Typography
        variant="code"
        as="div"
        className="ndl-ai-preview-header-leading-content"
      >
        {children}
      </Typography>

      {actions && (
        <div className="ndl-ai-preview-header-actions">{actions}</div>
      )}
    </div>
  );
};

type PreviewConfirmationProps = {
  /** The content to display in the confirmation */
  children?: React.ReactNode;
  /** The actions to display in the confirmation, usually two buttons; one for confirming and one for rejecting. */
  actions: React.ReactNode;
  /** Whether the confirmation is used as a footer */
  isFooter?: boolean;
  /** The status of the confirmation */
  status?: 'pending' | 'confirmed' | 'rejected';
  /** The text to display for the confirmed status */
  confirmedActionText?: string;
  /** The text to display for the rejected status */
  rejectedActionText?: string;
};

const PreviewConfirmation = ({
  children,
  actions,
  isFooter = false,
  status = 'pending',
  confirmedActionText = 'Action confirmed',
  rejectedActionText = 'Action rejected',
  ref,
  style,
  className,
  htmlAttributes,
  ...restProps
}: CommonProps<'div', PreviewConfirmationProps>) => {
  const classes = classNames('ndl-ai-preview-confirmation', className, {
    'ndl-ai-preview-confirmation-footer': isFooter,
  });

  return (
    <div
      ref={ref}
      className={classes}
      style={style}
      {...restProps}
      {...htmlAttributes}
    >
      {!(isFooter && status !== 'pending') && (
        <Typography
          as="div"
          variant={isFooter ? 'subheading-small' : 'body-medium'}
          className="ndl-ai-preview-confirmation-content"
        >
          {children}
        </Typography>
      )}
      <Typography
        variant="body-medium"
        as="div"
        className="ndl-ai-preview-confirmation-actions"
      >
        {status === 'pending' && actions}
        {status === 'confirmed' && (
          <div className="ndl-ai-preview-confirmation-confirm-reject">
            <CheckIconOutline className="ndl-ai-preview-confirmation-action-icon" />
            {confirmedActionText}
          </div>
        )}
        {status === 'rejected' && (
          <div className="ndl-ai-preview-confirmation-confirm-reject">
            <XMarkIconOutline className="ndl-ai-preview-confirmation-action-icon" />
            {rejectedActionText}
          </div>
        )}
      </Typography>
    </div>
  );
};

type PreviewCodeProps = {
  /** The code content to display */
  code: string;
  /** The language of the code (e.g., 'cypher', 'python', 'javascript') */
  language: React.ComponentProps<typeof CodeBlock>['language'];
  /** The current state of the code block */
  label?: 'read' | 'write';
  /** Whether the code block is loading */
  isLoading?: boolean;
  /** The actions to display in the header. Should be small buttons or icon buttons only. */
  headerActions?: React.ReactNode;
  /** The theme of the code block */
  theme?: 'ndl-code-dark' | 'ndl-code-light';
};

const PreviewCode = ({
  code,
  language,
  label,
  isLoading = false,
  headerActions,
  theme,
  ref,
  style,
  className,
  htmlAttributes,
  ...restProps
}: CommonProps<'div', PreviewCodeProps>) => {
  const [loadedLanguage, setLoadedLanguage] = useState<
    PreviewCodeProps['language'] | null
  >(null);
  const { theme: ndlTheme } = useNeedleTheme();

  useEffect(() => {
    if (!language || !(Languages as readonly string[]).includes(language)) {
      setLoadedLanguage('text');
      return;
    }

    loadLanguage(language as LanguageProp)
      .then((module) => {
        SyntaxHighlighter.registerLanguage(language, module.default);
        setLoadedLanguage(language);
      })
      .catch((err) => console.error(err));
  }, [language]);

  const getTheme = () => {
    switch (theme) {
      case 'ndl-code-dark':
        return ndlCodeDark;
      case 'ndl-code-light':
        return ndlCodeLight;
      default:
        return ndlTheme === 'light' ? ndlCodeLight : ndlCodeDark;
    }
  };

  return (
    <div
      ref={ref}
      style={style}
      className={classNames('ndl-ai-preview-code', className)}
      {...restProps}
      {...htmlAttributes}
    >
      {isLoading && (
        <div className="ndl-ai-preview-code-loading">
          <LoadingSpinner size="medium" />
          <Typography variant="code">
            WRITING {language.toUpperCase()}
          </Typography>
        </div>
      )}
      {!isLoading && (
        <>
          <PreviewHeader
            className="ndl-ai-preview-code-header"
            actions={
              <>
                <ClipboardButton
                  textToCopy={code}
                  type="clean-icon-button"
                  size="small"
                />
                {headerActions}
              </>
            }
          >
            {language} {language.toLowerCase() === 'cypher' ? 'QUERY' : ''}
            {label && (
              <StatusLabel
                variant={label === 'write' ? 'warning' : 'info'}
                size="small"
                fill="semi-filled"
              >
                {label.toUpperCase()}
              </StatusLabel>
            )}
          </PreviewHeader>
          <div className="ndl-ai-preview-code-content">
            <SyntaxHighlighter
              language={loadedLanguage ?? 'text'}
              style={{
                ...getTheme(),
                'pre[class*="language-"]': {
                  border: 0,
                  color: 'var(--theme-color-neutral-text-default)',
                  lineHeight: '1',
                  overflowX: 'auto',
                  padding: 'var(--space-12)',
                  width: 'fit-content',
                },
              }}
              // Turn on 'shouldShowLineNumbers' & 'wrapLongLines' at the same time, the display is wrong
              // https://github.com/react-syntax-highlighter/react-syntax-highlighter/issues/402
              // wrapLongLines
              codeTagProps={{ className: 'n-ai-preview-code' }}
              showLineNumbers={false}
            >
              {code}
            </SyntaxHighlighter>
          </div>
        </>
      )}
    </div>
  );
};

export const Preview = Object.assign(PreviewComponent, {
  Code: PreviewCode,
  Confirmation: PreviewConfirmation,
  Header: PreviewHeader,
});
